
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * NewsProfile struct
 */
class NewsProfile {
  /**
   * Initialize struct
   * @param {!UUID=} id
   * @param {!string=} name
   * @param {!string=} comment
   * @param {!string=} description
   * @param {!Date=} time
   * @param {!number=} beforeSeconds
   * @param {!number=} afterSeconds
   * @param {!UUID=} marginRateProfileId
   * @constructor
   */
  constructor (id = UUID.sequential(), name = '', comment = '', description = '', time = new Date(0), beforeSeconds = new UInt64(0, 0), afterSeconds = new UInt64(0, 0), marginRateProfileId = new UUID()) {
    this.id = id
    this.Name = name
    this.Comment = comment
    this.Description = description
    this.Time = time
    this.BeforeSeconds = beforeSeconds
    this.AfterSeconds = afterSeconds
    this.MarginRateProfileId = marginRateProfileId
  }

  /**
   * Copy struct (shallow copy)
   * @this {!NewsProfile}
   * @param {!NewsProfile} other Other struct
   * @returns {!NewsProfile} This struct
   */
  copy (other) {
    if (other.id != null) {
      this.id = new UUID(other.id)
    } else {
      this.id = null
    }
    if (other.Name != null) {
      this.Name = other.Name
    } else {
      this.Name = null
    }
    if (other.Comment != null) {
      this.Comment = other.Comment
    } else {
      this.Comment = null
    }
    if (other.Description != null) {
      this.Description = other.Description
    } else {
      this.Description = null
    }
    if (other.Time != null) {
      if (other.Time instanceof Date) {
        this.Time = new Date(other.Time.getTime())
      } else {
        this.Time = new Date(Math.round(other.Time / 1000000))
      }
    } else {
      this.Time = null
    }
    if (other.BeforeSeconds != null) {
      this.BeforeSeconds = UInt64.fromNumber(other.BeforeSeconds)
    } else {
      this.BeforeSeconds = null
    }
    if (other.AfterSeconds != null) {
      this.AfterSeconds = UInt64.fromNumber(other.AfterSeconds)
    } else {
      this.AfterSeconds = null
    }
    if (other.MarginRateProfileId != null) {
      this.MarginRateProfileId = new UUID(other.MarginRateProfileId)
    } else {
      this.MarginRateProfileId = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!NewsProfile}
   * @returns {!NewsProfile} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new NewsProfileModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new NewsProfileModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!NewsProfile}
   * @param {!NewsProfile} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof NewsProfile)) {
      throw new TypeError('Instance of NewsProfile is required!')
    }
    // noinspection RedundantIfStatementJS
    if (this.id != null) {
      if ((other.id == null) || !this.id.eq(other.id)) {
        return false
      }
    } else if (other.id != null) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!NewsProfile}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      id: ((this.id != null) ? this.id.toString() : null),
      Name: ((this.Name != null) ? this.Name : null),
      Comment: ((this.Comment != null) ? this.Comment : null),
      Description: ((this.Description != null) ? this.Description : null),
      Time: ((this.Time != null) ? (this.Time.getTime() * 1000000) : null),
      BeforeSeconds: ((this.BeforeSeconds != null) ? this.BeforeSeconds.toNumber() : null),
      AfterSeconds: ((this.AfterSeconds != null) ? this.AfterSeconds.toNumber() : null),
      MarginRateProfileId: ((this.MarginRateProfileId != null) ? this.MarginRateProfileId.toString() : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return NewsProfile.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!NewsProfile} other Object value
   * @returns {!NewsProfile} Created struct
   */
  static fromObject (other) {
    return new NewsProfile().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!NewsProfile}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return NewsProfile.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!NewsProfile}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 93
  }
}

export { NewsProfile };

/**
 * Fast Binary Encoding NewsProfile field model
 */
class FieldModelNewsProfile extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.id = new fbe.FieldModelUUID(buffer, 4 + 4)
    this.Name = new fbe.FieldModelString(buffer, this.id.fbeOffset + this.id.fbeSize)
    this.Comment = new fbe.FieldModelString(buffer, this.Name.fbeOffset + this.Name.fbeSize)
    this.Description = new fbe.FieldModelString(buffer, this.Comment.fbeOffset + this.Comment.fbeSize)
    this.Time = new fbe.FieldModelTimestamp(buffer, this.Description.fbeOffset + this.Description.fbeSize)
    this.BeforeSeconds = new fbe.FieldModelUInt64(buffer, this.Time.fbeOffset + this.Time.fbeSize)
    this.AfterSeconds = new fbe.FieldModelUInt64(buffer, this.BeforeSeconds.fbeOffset + this.BeforeSeconds.fbeSize)
    this.MarginRateProfileId = new fbe.FieldModelUUID(buffer, this.AfterSeconds.fbeOffset + this.AfterSeconds.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelNewsProfile}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelNewsProfile}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.id.fbeSize + this.Name.fbeSize + this.Comment.fbeSize + this.Description.fbeSize + this.Time.fbeSize + this.BeforeSeconds.fbeSize + this.AfterSeconds.fbeSize + this.MarginRateProfileId.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelNewsProfile}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.id.fbeExtra + this.Name.fbeExtra + this.Comment.fbeExtra + this.Description.fbeExtra + this.Time.fbeExtra + this.BeforeSeconds.fbeExtra + this.AfterSeconds.fbeExtra + this.MarginRateProfileId.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelNewsProfile}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelNewsProfile.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelNewsProfile}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 93
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelNewsProfile}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelNewsProfile}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.id.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.id.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.id.fbeSize

    if ((fbeCurrentSize + this.Name.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Name.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Name.fbeSize

    if ((fbeCurrentSize + this.Comment.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Comment.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Comment.fbeSize

    if ((fbeCurrentSize + this.Description.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Description.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Description.fbeSize

    if ((fbeCurrentSize + this.Time.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Time.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Time.fbeSize

    if ((fbeCurrentSize + this.BeforeSeconds.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.BeforeSeconds.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.BeforeSeconds.fbeSize

    if ((fbeCurrentSize + this.AfterSeconds.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.AfterSeconds.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AfterSeconds.fbeSize

    if ((fbeCurrentSize + this.MarginRateProfileId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.MarginRateProfileId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MarginRateProfileId.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelNewsProfile}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelNewsProfile}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelNewsProfile}
   * @param {!NewsProfile} fbeValue Default value, defaults is new NewsProfile()
   * @returns {!NewsProfile} NewsProfile value
   */
  get (fbeValue = new NewsProfile()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelNewsProfile}
   * @param {!NewsProfile} fbeValue NewsProfile value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.id.fbeSize) <= fbeStructSize) {
      fbeValue.id = this.id.get(UUID.sequential())
    } else {
      fbeValue.id = UUID.sequential()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.id.fbeSize

    if ((fbeCurrentSize + this.Name.fbeSize) <= fbeStructSize) {
      fbeValue.Name = this.Name.get()
    } else {
      fbeValue.Name = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Name.fbeSize

    if ((fbeCurrentSize + this.Comment.fbeSize) <= fbeStructSize) {
      fbeValue.Comment = this.Comment.get()
    } else {
      fbeValue.Comment = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Comment.fbeSize

    if ((fbeCurrentSize + this.Description.fbeSize) <= fbeStructSize) {
      fbeValue.Description = this.Description.get()
    } else {
      fbeValue.Description = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Description.fbeSize

    if ((fbeCurrentSize + this.Time.fbeSize) <= fbeStructSize) {
      fbeValue.Time = this.Time.get()
    } else {
      fbeValue.Time = new Date(0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Time.fbeSize

    if ((fbeCurrentSize + this.BeforeSeconds.fbeSize) <= fbeStructSize) {
      fbeValue.BeforeSeconds = this.BeforeSeconds.get()
    } else {
      fbeValue.BeforeSeconds = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.BeforeSeconds.fbeSize

    if ((fbeCurrentSize + this.AfterSeconds.fbeSize) <= fbeStructSize) {
      fbeValue.AfterSeconds = this.AfterSeconds.get()
    } else {
      fbeValue.AfterSeconds = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AfterSeconds.fbeSize

    if ((fbeCurrentSize + this.MarginRateProfileId.fbeSize) <= fbeStructSize) {
      fbeValue.MarginRateProfileId = this.MarginRateProfileId.get()
    } else {
      fbeValue.MarginRateProfileId = new UUID()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MarginRateProfileId.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelNewsProfile}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelNewsProfile}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelNewsProfile}
   * @param {!NewsProfile} fbeValue NewsProfile value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelNewsProfile}
   * @param {NewsProfile} fbeValue NewsProfile value
   */
  setFields (fbeValue) {
    this.id.set(fbeValue.id)
    this.Name.set(fbeValue.Name)
    this.Comment.set(fbeValue.Comment)
    this.Description.set(fbeValue.Description)
    this.Time.set(fbeValue.Time)
    this.BeforeSeconds.set(fbeValue.BeforeSeconds)
    this.AfterSeconds.set(fbeValue.AfterSeconds)
    this.MarginRateProfileId.set(fbeValue.MarginRateProfileId)
  }
}

export { FieldModelNewsProfile };

/**
 * Fast Binary Encoding NewsProfile model
 */
class NewsProfileModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelNewsProfile(this.buffer, 4)
  }

  /**
   * Get the NewsProfile model
   * @this {!NewsProfileModel}
   * @returns {!FieldModelNewsProfile} model NewsProfile model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!NewsProfileModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!NewsProfileModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return NewsProfileModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!NewsProfileModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelNewsProfile.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!NewsProfileModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!NewsProfileModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!NewsProfileModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!NewsProfileModel}
   * @param {!NewsProfile} value NewsProfile value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!NewsProfileModel}
   * @param {!NewsProfile} value NewsProfile value, defaults is new NewsProfile()
   * @return {!object} Deserialized NewsProfile value and its size
   */
  deserialize (value = new NewsProfile()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new NewsProfile(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new NewsProfile(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!NewsProfileModel}
   * @param {!number} prev Previous NewsProfile model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { NewsProfileModel };
